import {AppShell} from "@mantine/core";
import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {chatApi} from "./chatApi";
import classes from "./App.module.css";
import Navbar from "./components/Navbar";
import {useNavbarWidth} from "./hooks/useNavbarWidth";
import {readLocalStorageValue, useMediaQuery} from "@mantine/hooks";
import {bffApi} from "./bffApi";
import {
    ChatEngine,
    KAIResponseType,
    LocalStorageKeys,
} from "../../types";
import i18n from "../../i18n";
import {AppContext} from "./AppContext";
import {setUser} from "@sentry/react";
import {
    initializeChatEngineFromLocalStorage,
    initializeResponseTypeFromLocalStorage,
} from "./sessionSlice";
import {useAppDispatch} from "../../app/hooks";
import {AppShellMain} from "./components/AppShellMain";
import {useApplyUserLanguage} from "./useApplyUserLanguage";
import {BackgroundStreamNotifier} from "./components/BackgroundStreamNotifier";
import {isDashboardPath} from "../../paths";

export function App() {
    const isDesktop = useMediaQuery(`(min-width: 48em)`);
    const [mobileNavBarOpen, setMobileNavBarOpen] = useState(false);
    const [desktopNavBarOpen, setDesktopNavBarOpen] = useState(false);

    const location = useLocation();
    useEffect(() => {
        if (isDashboardPath(location.pathname)) return;
        setDesktopNavBarOpen(false);
        setMobileNavBarOpen(false);
    }, [location.pathname]);

    const userData = bffApi.endpoints.getUser.useQuery();
    const dispatch = useAppDispatch();

    // Initialize app data - populates getUserThreads, quickSearch, and getSettings caches
    const initData = chatApi.endpoints.getInit.useQuery({threadCount: 3});

    useApplyUserLanguage();

    const responseType = readLocalStorageValue({
        key: LocalStorageKeys.ResponseType,
        defaultValue: KAIResponseType.Fast,
    });
    const chatEngine = readLocalStorageValue({
        key: LocalStorageKeys.ChatEngine,
        defaultValue: ChatEngine.Standard,
    });
    useEffect(() => {
        if (userData.fulfilledTimeStamp) {
            setUser({
                email: userData.data?.epost,
                id: userData.data?.brukerKey,
                username: userData.data?.navn,
            });
        }
    }, [userData.fulfilledTimeStamp]);

    useEffect(() => {
        const updateDocumentLang = () => {
            document.documentElement.lang = i18n.language;
        };

        updateDocumentLang();
        i18n.on("languageChanged", updateDocumentLang);

        return () => {
            i18n.off("languageChanged", updateDocumentLang);
        };
    }, []);

    useEffect(() => {
        dispatch(initializeResponseTypeFromLocalStorage());
    }, [responseType]);

    useEffect(() => {
        dispatch(initializeChatEngineFromLocalStorage());
    }, [chatEngine]);

    const toggleNavBar = () => {
        if (isDesktop) {
            setDesktopNavBarOpen(!desktopNavBarOpen);
        } else {
            setMobileNavBarOpen(!mobileNavBarOpen);
        }
    };

    const navbarExpanded = isDesktop ? desktopNavBarOpen : mobileNavBarOpen;

    const {expanded: navbarWidthExpanded, collapsed: navbarWidthCollapsed} =
        useNavbarWidth();
    const navbarWidth = navbarExpanded
        ? navbarWidthExpanded
        : navbarWidthCollapsed;

    return (
        <AppContext.Provider
            value={{
                desktopNavBarOpen,
                setDesktopNavBarOpen,
                mobileNavBarOpen,
                setMobileNavBarOpen,
                toggleNavBar,
                navbarExpanded,
            }}
        >
            <AppShell
                className={classes.appShell}
                navbar={{
                    width: navbarWidth,
                    breakpoint: "xs",
                    collapsed: {
                        mobile: !mobileNavBarOpen,
                        desktop: false,
                    },
                }}
            >
                <AppShell.Navbar>
                    <Navbar />
                </AppShell.Navbar>
                <AppShell.Main className={classes.main}>
                    <AppShellMain />
                </AppShell.Main>
            </AppShell>
            <BackgroundStreamNotifier />
        </AppContext.Provider>
    );
}

export default App;
